/**
 * CRM internal user notification policy by role + team.
 * Keep in sync with functions/notificationInternalPolicy.js
 */

export const FULL_INTERNAL_NOTIFICATION_ROLES = new Set(['Owner', 'Executive']);

/** Keys any restricted (non–Owner/Executive) internal user may toggle and receive via org-wide queries. */
export const RESTRICTED_INTERNAL_BASE_NOTIFICATION_KEYS = [
  'newMessage',
  'newQuestion',
  'newTask',
  'communityPost',
];

const TEAM_TO_FULFILLMENT_NOTIFICATION = {
  Technology: 'technologyFulfillment',
  HR: 'hrFulfillment',
  Marketing: 'marketingFulfillment',
  General: 'generalFulfillment',
  Sales: 'generalFulfillment',
  Operations: 'generalFulfillment',
  Finance: 'generalFulfillment',
};

export const ALL_CRM_NOTIFICATION_KEYS = [
  'newLead',
  'newClient',
  'newMessage',
  'newQuestion',
  'newTask',
  'technologyFulfillment',
  'hrFulfillment',
  'marketingFulfillment',
  'generalFulfillment',
  'communityPost',
  'ndaSigned',
  'contractSigned',
  'portalSignup',
  'summitRegistration',
  'flaggedLead',
  'assessmentComplete',
  'clientCheckInComplete',
];

export function isFullInternalNotificationRole(role) {
  return FULL_INTERNAL_NOTIFICATION_ROLES.has(role || '');
}

/**
 * Fulfillment email key for this team (Sales/Operations/Finance → general).
 */
export function getTeamFulfillmentNotificationKey(team) {
  const t = (team || '').trim();
  if (!t) return 'generalFulfillment';
  return TEAM_TO_FULFILLMENT_NOTIFICATION[t] || 'generalFulfillment';
}

/**
 * @returns {Set<string>|null} null = unrestricted (all keys); Set = allowed keys only
 */
export function getAllowedNotificationKeysForInternalUser(role, team) {
  if (isFullInternalNotificationRole(role)) return null;
  const fulfillmentKey = getTeamFulfillmentNotificationKey(team);
  return new Set([...RESTRICTED_INTERNAL_BASE_NOTIFICATION_KEYS, fulfillmentKey]);
}

/**
 * For recipients loaded from `internal_users` only (not portal `users`).
 * Missing role defaults to Member for policy.
 */
export function applyInternalStaffBroadcastPolicy(userData, notificationType) {
  if (!userData || typeof userData !== 'object') return true;
  const role = userData.role || 'Member';
  if (isFullInternalNotificationRole(role)) return true;

  const allowed = getAllowedNotificationKeysForInternalUser(role, userData.team);
  // Bulk newTask is never for restricted staff — assignee-only flow.
  if (notificationType === 'newTask') return false;

  return allowed.has(notificationType);
}

/**
 * Merge stored preferences with policy: restricted users cannot keep true on disallowed keys.
 */
export function buildNotificationsForInternalProfile(role, team, stored = {}) {
  const baseDefaults = {};
  ALL_CRM_NOTIFICATION_KEYS.forEach((k) => {
    baseDefaults[k] = true;
  });

  if (isFullInternalNotificationRole(role)) {
    return { ...baseDefaults, ...stored };
  }

  const allowed = getAllowedNotificationKeysForInternalUser(role, team);
  const next = {};
  ALL_CRM_NOTIFICATION_KEYS.forEach((k) => {
    if (allowed.has(k)) {
      next[k] = stored[k] !== false;
    } else {
      next[k] = false;
    }
  });
  return next;
}
